import React, { useEffect } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { useParams } from 'util/reactRouterHooks';
import { getBuildingByUrlSlug, HasUrlSlug } from 'selectors/buildingsSelectors';
import { MenuItem, useMenu, useSession } from 'hooks';
import { Permission } from 'shared/permissions';
import { BuildingCredentialsPage } from '../credentials/buildingCredentialsPage/buildingCredentialsPage';
import { BuildingUsersPage } from '../users/buildingUsersPage/buildingUsersPage';
import { BuildingAddressPage } from '../address/buildingAddressPage/BuildingAddressPage';
import { BuildingOrgsPage } from '../orgs/buildingOrgsPage/BuildingOrgsPage';
import { BuildingEcsPage } from '../ecs/buildingEcsPage/BuildingEcsPage';
import { useSelector } from 'react-redux';
import { State } from 'reducers';

export const BuildingSettingsRouter = () => {
    const params = useParams<HasUrlSlug>();
    const { urlSlug } = params;

    const { hasPermission } = useSession();
    const { pushMenu, popMenu } = useMenu();

    const building = useSelector((state: State) => getBuildingByUrlSlug(state, params));
    const hasEcs = building && building.hasEcs;
    const buildingId = building && building.id;

    //
    // Create/Update the buildings menu when the BuildingRouter is
    // mounted, the building changes, or the user permissions change
    //
    useEffect(() => {
        const items: MenuItem[] = [];

        if (hasPermission(buildingId, Permission.EditBuilding)) {
            items.push({ id: 'building-address-link', link: `/buildings/${urlSlug}/settings/address`, textKey: 'BUILDING.ADDRESS.LINK' });
        }
        if (hasPermission(buildingId, Permission.ViewUsers)) {
            items.push({ id: 'building-users-link', link: `/buildings/${urlSlug}/settings/users`, textKey: 'BUILDING.USERS.LINK' });
        }
        if (hasPermission(buildingId, Permission.EditBuilding)) {
            items.push({ id: 'building-orgs-link', link: `/buildings/${urlSlug}/settings/organizations`, textKey: 'ORGS.LINK' });
            items.push({ id: 'building-creds-link', link: `/buildings/${urlSlug}/settings/credentials`, textKey: 'BUILDING.CREDENTIALS.LINK' });
        }
        if (hasEcs && (hasPermission(buildingId, Permission.EditBuilding) || hasPermission(buildingId, Permission.EditBuildingFeatures))) {
            items.push({ id: 'building-ecs-link', link: `/buildings/${urlSlug}/settings/ecs`, textKey: 'BUILDING.ECS.LINK' });
        }

        pushMenu({ id: 'menu-building-settings', textKey: 'BUILDING.SETTINGS.LINK', items }, 2);

        // Remove the menu when we are unmounted
        return () => popMenu(2);
    }, [ urlSlug, hasEcs, buildingId, pushMenu, popMenu, hasPermission ]);

    if (!hasPermission(buildingId, Permission.EditBuilding) && !hasPermission(buildingId, Permission.EditBuildingFeatures)) {
        return null;
    }
    return (
        <Switch>
            { hasPermission(buildingId, Permission.EditBuilding) &&
                <Route path="/buildings/:urlSlug/settings/address" component={ BuildingAddressPage }/>
            }
            { hasPermission(buildingId, Permission.EditBuilding) &&
                <Route path="/buildings/:urlSlug/settings/credentials" component={ BuildingCredentialsPage }/>
            }
            { hasPermission(buildingId, Permission.EditBuilding) &&
                <Route path="/buildings/:urlSlug/settings/organizations" component={ BuildingOrgsPage }/>
            }
            <Route path="/buildings/:urlSlug/settings/users" component={ BuildingUsersPage }/>
            { (hasPermission(buildingId, Permission.EditBuilding) || hasPermission(buildingId, Permission.EditBuildingFeatures)) &&
                <Route path="/buildings/:urlSlug/settings/ecs" component={ BuildingEcsPage }/>
            }

            <Redirect to={ `/buildings/${urlSlug}/settings/address` }/>
        </Switch>
    );
};
